import React, { useState, useEffect } from 'react';
import OnlineMenu from './OnlineMenu';
import { getActivePromotions } from 'apis/menuAPIs';
import { useParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery } from '@tanstack/react-query';
import PromotionPage from './PromotionPage';
import { Paper, Typography, Button, Box } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

const OnlineEntry = (props) => {
  const { menuId, tableId } = useParams();
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    // Check localStorage for saved menu preference
    const savedPreference = localStorage.getItem('viewMenuPreference');
    if (savedPreference) {
      try {
        const { timestamp, menuId: savedMenuId } = JSON.parse(savedPreference);
        const savedPreferenceMinutesInMs = 5 * 60 * 1000;
        
        // If preference exists, is for the same menu, and is less than 30 minutes old
        if (savedMenuId === menuId && Date.now() - timestamp < savedPreferenceMinutesInMs) {
          setShowMenu(true);
        } else {
          setShowMenu(false);
        }
      } catch (e) {
        console.error("Error parsing saved preference:", e);
      }
    }
  }, [menuId]);

  const {
    data: promotionDetails,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['activePromotions', menuId],
    queryFn: () => getActivePromotions(menuId, tableId),
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!menuId,
  });

  const handleViewMenu = () => {
    setShowMenu(true);
    
    // Save preference with current timestamp and menuId
    const preference = {
      timestamp: Date.now(),
      menuId
    };
    localStorage.setItem('viewMenuPreference', JSON.stringify(preference));
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </div>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Paper elevation={2} style={{ padding: '24px', maxWidth: '400px', textAlign: 'center' }}>
          <ErrorOutlineIcon style={{ fontSize: 48, color: '#e53935', marginBottom: '16px' }} />
          <Typography variant="h6" gutterBottom>Unable to Load Menu</Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            {error?.message || 'An unexpected error occurred while loading the menu.'}
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            Try Again
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!showMenu && promotionDetails && promotionDetails.promotionCount > 0) {
    return (
      <PromotionPage
        image={promotionDetails.promotionImage}
        title={promotionDetails.restaurantName}
        description={promotionDetails.restaurantAltName}
        merchantId={menuId}
        buttonText="Order Now"
        onButtonClick={handleViewMenu}
        tableNumber={tableId}
        onPromotions={() => {
          // Handle promotions button click - you can implement this as needed
          // For now we'll just go to the menu
          setShowMenu(true);
          
          // Save preference with current timestamp and menuId
          const preference = {
            timestamp: Date.now(),
            menuId
          };
          localStorage.setItem('viewMenuPreference', JSON.stringify(preference));
        }}
        promotionDetails={promotionDetails}
      />
    );
  }

  return <OnlineMenu {...props} />;
};

export default OnlineEntry;